/**
 * The address's map — `docs/new-request.md` §7.1, Board 5.
 *
 * 150 px (120 for an address Google could not confirm, 110 on a phone), on
 * the Google tiles base — never `plain`, which is a blank fill, because
 * Google's terms want a Places result shown on a Google map. No controls: the
 * pin, its label, a scale bar sized to the distance it names, and the credit.
 * One instance in the sheet.
 *
 * It is not `MapCanvas`. That one is the property map's editor and its host
 * holds a 240 px floor so a plan is never drawn into a sliver; this map is a
 * glance at where a pin is. It uses the same lower pieces — the imagery
 * session from `lib/map-provider`, MapLibre by dynamic `import()` with its
 * worker URL from Vite — so the library is still its own chunk, fetched the
 * first time an address is chosen.
 *
 * **Typed by hand, the pin drags** (§7.1): the map is centred on the town and
 * the person puts the pin on the house. A drop is told to `onMove` with the
 * point; nothing here decides what the property's coordinates are.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { Map as MapLibreMap, Marker as MapLibreMarker, StyleSpecification } from 'maplibre-gl'
import { metresPerPixel, scaleBar } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { tokenColour } from '../lib/token-colour.js'
import { imagerySession, type ImagerySession } from '../lib/map-provider.js'

export interface PinPoint { lat: number; lng: number }

/**
 * Close enough to read the block a house is on, far enough to see the town —
 * where Board 5's bar reads *500 ft* at about 42 px.
 */
const ZOOM = 15

export function PinMap({ point, label, height, draggable = false, onMove }: {
  point: PinPoint
  /** The pill beside the pin: the street line, or *drag to the house*. Null for none. */
  label: string | null
  height: number
  draggable?: boolean
  onMove?: (point: PinPoint) => void
}) {
  const host = useRef<HTMLDivElement>(null)
  const map = useRef<MapLibreMap | null>(null)
  const marker = useRef<MapLibreMarker | null>(null)
  const [pinNode] = useState(() => (typeof document === 'undefined' ? null : document.createElement('div')))
  const [session, setSession] = useState<ImagerySession | null>(null)
  const [failed, setFailed] = useState(false)
  const [ready, setReady] = useState(false)
  const [bar, setBar] = useState<{ label: string; pixels: number } | null>(null)
  const moved = useRef(onMove)
  moved.current = onMove

  /* The tiles first; without a session the map draws on the page's own fill. */
  useEffect(() => {
    let live = true
    imagerySession()
      .then((got) => { if (live) setSession(got) })
      .catch(() => { if (live) setFailed(true) })
    return () => { live = false }
  }, [])

  /* Then the map, once for the life of this box. */
  useEffect(() => {
    if ((!session && !failed) || !host.current || map.current || !pinNode) return undefined
    let cancelled = false
    let made: MapLibreMap | null = null

    void (async () => {
      const maplibre = await import('maplibre-gl')
      await import('maplibre-gl/dist/maplibre-gl.css')
      maplibre.setWorkerUrl((await import('maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url')).default)
      if (cancelled || !host.current) return

      made = new maplibre.Map({
        container: host.current,
        style: styleOf(session),
        center: [point.lng, point.lat],
        zoom: ZOOM,
        maxZoom: session ? Math.min(20, session.maxZoom) : 20,
        dragRotate: false,
        touchPitch: false,
        pitchWithRotate: false,
        renderWorldCopies: false,
        attributionControl: false,
        /* A map inside a scrolling sheet must not take the wheel from it. */
        scrollZoom: false,
        keyboard: false,
        pixelRatio: Math.min(2, window.devicePixelRatio || 1),
      })
      map.current = made
      made.setPadding(paddingFor(height))
      const pin = new maplibre.Marker({ element: pinNode, anchor: 'bottom', draggable })
        .setLngLat([point.lng, point.lat])
        .addTo(made)
      pin.on('dragend', () => {
        const at = pin.getLngLat()
        moved.current?.({ lat: at.lat, lng: at.lng })
      })
      marker.current = pin
      const measure = () => {
        if (!made) return
        setBar(scaleBar(metresPerPixel(made.getCenter().lat, made.getZoom()), 60))
      }
      made.on('move', measure)
      made.on('load', () => { if (!cancelled) { measure(); setReady(true) } })
    })()

    return () => {
      cancelled = true
      marker.current?.remove()
      marker.current = null
      made?.remove()
      if (map.current === made) map.current = null
      setReady(false)
    }
    /*
     * Built once per session. The point is the *initial* camera — later
     * points move the map below — and `draggable` is set below as well.
     */
  }, [session, failed, pinNode])

  /* A new address moves the pin and the camera rather than rebuilding the map. */
  useEffect(() => {
    if (!map.current || !marker.current) return
    const here = marker.current.getLngLat()
    if (Math.abs(here.lat - point.lat) < 1e-9 && Math.abs(here.lng - point.lng) < 1e-9) return
    marker.current.setLngLat([point.lng, point.lat])
    map.current.jumpTo({ center: [point.lng, point.lat] })
  }, [point.lat, point.lng, ready])

  useEffect(() => { marker.current?.setDraggable(draggable) }, [draggable, ready])

  /* 150 ↔ 120 as Google confirms or not: the pin keeps its place in the box. */
  useEffect(() => {
    if (!map.current) return
    map.current.setPadding(paddingFor(height))
    map.current.jumpTo({ center: [point.lng, point.lat] })
  }, [height, ready])

  return (
    <div
      className="relative overflow-hidden rounded-[12px] border border-line bg-surface-group"
      /* `height` is the map's own, inside the border (Board 5: 150 within its 1 px rule). */
      style={{ height: height + 2 }}
    >
      {/* `h-full w-full` as well: MapLibre's stylesheet makes its container `position: relative`. */}
      <div ref={host} className="absolute inset-0 h-full w-full" />
      {pinNode && createPortal(<PinGlyph label={label} draggable={draggable} />, pinNode)}
      {bar && (
        <div aria-hidden className="absolute bottom-2 right-3 flex items-center gap-1.5">
          <span
            className="block h-[7px] border-2 border-t-0 border-text-muted"
            style={{ width: `${Math.round(bar.pixels)}px` }}
          />
          <span className="text-[10px] text-text-muted">{bar.label}</span>
        </div>
      )}
      {session?.attribution && (
        <span className="absolute bottom-1.5 left-2 text-[9.5px] text-text-faint">
          © {session.attribution}
        </span>
      )}
    </div>
  )
}

/**
 * The board's pin: a green drop with a white dot in a soft halo, and the
 * label pill to its right at the height of the pin's head.
 */
function PinGlyph({ label, draggable }: { label: string | null; draggable: boolean }) {
  return (
    <span className={cn('relative block h-[35px] w-6', draggable && 'cursor-grab')}>
      <span aria-hidden className="absolute -left-2.5 -top-0.5 block h-11 w-11 rounded-full bg-accent/[.12]" />
      <svg aria-hidden viewBox="-1 -1 24 35" width="24" height="35" className="relative block text-accent">
        <path d="M11 32 C2 20 0 15 0 11 A11 11 0 1 1 22 11 C22 15 20 20 11 32z"
          fill="currentColor" stroke="rgb(var(--color-surface))" strokeWidth="2" />
        <circle cx="11" cy="11" r="4" fill="rgb(var(--color-surface))" />
      </svg>
      {label && (
        <span className={cn('absolute left-7 top-0 h-[22px] whitespace-nowrap rounded-[6px] border',
          'border-accent-line bg-surface px-[7px] text-[11.5px] font-bold leading-5 text-accent-dark')}>
          {label}
        </span>
      )}
    </span>
  )
}

/**
 * The boards stand the pin's tip 20 px below the middle (and, on Board 5's
 * 150 px map, 11 px right of it), so the head and its label sit in the middle
 * of the box rather than above it. Padding moves the point the camera centres
 * on, and every later `jumpTo` keeps it.
 */
function paddingFor(height: number): { top: number; bottom: number; left: number; right: number } {
  return { top: 40, bottom: 0, left: height >= 150 ? 22 : 0, right: 0 }
}

/** One raster source, the road map. */
function styleOf(session: ImagerySession | null): StyleSpecification {
  if (!session) {
    return {
      version: 8 as const,
      sources: {},
      layers: [{ id: 'plain', type: 'background' as const,
        paint: { 'background-color': tokenColour('surface-group') } }],
    }
  }
  return {
    version: 8 as const,
    sources: {
      base: {
        type: 'raster' as const,
        tiles: session.tiles.map,
        tileSize: 256,
        maxzoom: Math.min(20, session.maxZoom),
      },
    },
    layers: [{ id: 'base', type: 'raster' as const, source: 'base' }],
  }
}
